import type { AxiosError } from 'axios';
import type { AppError, ApiEnvelope, ApiFieldError } from '@/types/api';

/**
 * 把 axios 异常归一化为 AppError。
 * 后端 D-106 信封：{ success, code, label, message, trace_id, errors }
 */
export function normalizeError(error: unknown): AppError {
  // axios 错误
  const ax = error as AxiosError<Partial<ApiEnvelope<unknown>> & { label?: string }>;
  if (ax?.isAxiosError) {
    const body = ax.response?.data;
    const httpStatus = ax.response?.status ?? 0;

    if (body && typeof body === 'object') {
      return {
        code: typeof body.code === 'number' ? body.code : 0,
        label: typeof body.label === 'string' ? body.label : fallbackLabel(httpStatus),
        message: typeof body.message === 'string' ? body.message : fallbackMessage(httpStatus),
        traceId: typeof body.trace_id === 'string' ? body.trace_id : '',
        errors: Array.isArray(body.errors) ? (body.errors as ApiFieldError[]) : [],
        httpStatus,
        raw: error,
      };
    }

    // 网络错误 / 跨域 / 超时
    return {
      code: 0,
      label: 'NETWORK_ERROR',
      message: ax.code === 'ECONNABORTED' ? '请求超时，请重试' : '网络异常，请检查后重试',
      traceId: '',
      errors: [],
      httpStatus,
      raw: error,
    };
  }

  // 非 axios 错误（极少见）
  return {
    code: 0,
    label: 'UNKNOWN_ERROR',
    message: error instanceof Error ? error.message : '未知错误',
    traceId: '',
    errors: [],
    httpStatus: 0,
    raw: error,
  };
}

function fallbackLabel(status: number): string {
  switch (status) {
    case 401:
      return 'UNAUTHENTICATED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 422:
      return 'VALIDATION_FAILED';
    case 429:
      return 'RATE_LIMIT_EXCEEDED';
    default:
      return status >= 500 ? 'SYSTEM_ERROR' : 'UNKNOWN_ERROR';
  }
}

function fallbackMessage(status: number): string {
  switch (status) {
    case 401:
      return '请先登录';
    case 403:
      return '权限不足';
    case 404:
      return '资源不存在';
    case 422:
      return '请求参数校验失败';
    case 429:
      return '请求过于频繁，请稍后重试';
    default:
      return status >= 500 ? '系统繁忙，请稍后重试' : '请求失败';
  }
}
